"use client"

import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type Ref,
} from "react"
import { Field } from "@base-ui/react/field"
import { Form as FormPrimitive } from "@base-ui/react/form"
import { cn } from "cn"

function Form({
  ref,
  className,
  noValidate = true,
  autoComplete = "off",
  ...props
}: ComponentPropsWithoutRef<typeof FormPrimitive> & {
  ref?: Ref<ComponentRef<typeof FormPrimitive>>
}) {
  return (
    <FormPrimitive
      ref={ref}
      className={cn("flex flex-col gap-4", className)}
      noValidate={noValidate}
      autoComplete={autoComplete}
      {...props}
    />
  )
}

function FormField({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Field.Root> & {
  ref?: Ref<ComponentRef<typeof Field.Root>>
}) {
  return (
    <Field.Root
      ref={ref}
      className={cn("group/form-field relative", className)}
      {...props}
    />
  )
}

const FormControl = Field.Control

function FormLabel({
  ref,
  className,
  size = 1,
  ...props
}: ComponentPropsWithoutRef<typeof Field.Label> & {
  size?: 1 | 2
} & { ref?: Ref<ComponentRef<typeof Field.Label>> }) {
  return (
    <Field.Label
      ref={ref}
      className={cn(
        "mb-2 block text-base leading-tight font-semibold peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
        size === 2 && "mb-2.5 text-md",
        className
      )}
      {...props}
    />
  )
}

function FormError({
  ref,
  className,
  match = true,
  ...props
}: ComponentPropsWithoutRef<typeof Field.Error> & {
  ref?: Ref<ComponentRef<typeof Field.Error>>
}) {
  return (
    <Field.Error
      ref={ref}
      className={cn(
        "mt-1.5 block text-xs leading-tight font-normal text-destructive peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
        className
      )}
      match={match}
      {...props}
    />
  )
}

export { Form, FormControl, FormError, FormField, FormLabel }
